// resources/js/src/components/community/components/post/CreatePost.tsx
import React, { useState } from "react";
import PostCard from "./components/PostCard";
import PostModal from "./components/PostModal";
import Notification from "../../../common/Notification";
import { usePostStore, createPostStore } from "../../../../store/postStore";
import { PostAction } from "./interfaces/post.interface";
import { useAuthStore } from "../../../../auth/auth.store";

/*
|--------------------------------------------------------------------------
| Props
|--------------------------------------------------------------------------
| useStore (اختياري):
|   - لو متبعت (مثلاً من ProfilePage)، CreatePost هيستخدمها بدل الـ global
|     عشان البوست الجديد يظهر في نفس الـ feed (mine) فوراً.
|   - لو مش متبعت، هيستخدم usePostStore الـ global (feed العام).
|--------------------------------------------------------------------------
*/
type Props = {
  useStore?: ReturnType<typeof createPostStore>;
};

const CreatePost: React.FC<Props> = ({ useStore }) => {
  const [postText, setPostText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [postAction, setPostAction] = useState<PostAction>("default");

  /*
  |------------------------------------------------------------------
  | Store Selection
  |------------------------------------------------------------------
  | بنختار الـ store:
  |   - لو useStore متبعتة → نستخدمها (hook call)
  |   - غير كده → usePostStore الـ global
  |
  | ⚠️ لازم ننادي الـ hooks بنفس الترتيب في كل render.
  | عشان كده بننادي الاتنين ونختار واحد — الـ hook call نفسه
  | مش بيتأثر بالاختيار.
  |------------------------------------------------------------------
  */
  const globalStore = usePostStore();
  const store = useStore ? useStore() : globalStore;

  const createPost = store.createPost;
  const isSaving = store.isSaving;

  // Auth
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser); // (اختياري — لو هتستخدمه في placeholders)

  const toggleEmojiPicker = () => setEmojiPickerOpen((v) => !v);

  const addEmoji = (emoji: string) => setPostText((prev) => prev + emoji);

  /*
  |------------------------------------------------------------------
  | File Selection
  |------------------------------------------------------------------
  | ⚠️ إصلاح: في النسخة القديمة كان فيه تكرار في الـ previews
  | لأن reader.onload كان بيضيف preview + setPreviews كان بيضيف
  | newPreviews تاني. دلوقتي بنضيف الـ previews مرة واحدة بس.
  |------------------------------------------------------------------
  */
  const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);

    if (files.length + selected.length > 4) {
      showNotification("You can upload up to 4 files.");
      return;
    }

    const newPreviews: string[] = [];

    selected.forEach((file) => {
      if (file.type.startsWith("image/")) {
        // للصور: نستخدم FileReader عشان preview
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPreviews((prev) => [...prev, ev.target?.result as string]);
        };
        reader.readAsDataURL(file);
      } else {
        // للفيديو: نستخدم object URL
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    setFiles((prev) => [...prev, ...selected]);

    // ✅ نضيف newPreviews مرة واحدة بس (بتاعة الفيديو)
    if (newPreviews.length > 0) {
      setPreviews((prev) => [...prev, ...newPreviews]);
    }

    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setFiles([]);
    setPreviews([]);
  };

const publishPost = async (textOrEvent?: string | React.SyntheticEvent) => {
  // لو جاي event (من form submit أو button click) → نتجاهله ونستخدم postText
  const rawText =
    typeof textOrEvent === "string" ? textOrEvent : postText;
  const body = (rawText ?? "").trim();

  if (!body && files.length === 0) {
    showNotification("Please add text or a file to post");
    return;
  }

  if (!isAuthenticated) {
    showNotification("Please sign in to post.");
    return;
  }

  const post = await createPost({
    body: body || undefined,
    visibility: "public",
    media: files.length > 0 ? files : undefined,
  });

  if (post) {
    showNotification("Post published successfully! 🎉");
    setModalOpen(false);
    setPostText("");
    clearFiles();
  } else {
    showNotification("Failed to publish post. Please try again.");
  }
};

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const openPostModal = (action: PostAction) => {
    setPostAction(action);
    setModalOpen(true);
  };

  // Guests: show sign-in prompt
  if (!isAuthenticated) {
    return (
      <div className="rounded-lg bg-white p-4 shadow dark:bg-deepSlate">
        <p className="text-center text-gray-600 dark:text-gray-300">
          <a
            href="/login"
            className="font-semibold text-primary-600 hover:underline"
          >
            Sign in
          </a>{" "}
          to post.
        </p>
      </div>
    );
  }

  return (
    <>
      <PostCard
        onOpenModal={openPostModal}
        onSubmit={(content) => publishPost(content)}
      />
      <PostModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        postText={postText}
        setPostText={setPostText}
        files={files}
        previews={previews}
        emojiPickerOpen={emojiPickerOpen}
        toggleEmojiPicker={toggleEmojiPicker}
        addEmoji={addEmoji}
        handleFilesSelect={handleFilesSelect}
        removeFile={removeFile}
        clearFiles={clearFiles}
        publishPost={publishPost}
        isSaving={isSaving}
      />

      {notification && <Notification message={notification} />}
    </>
  );
};

export default CreatePost;